'use client'

import * as React from 'react'
import { Link } from '@repo/web-ui/components/link'
import { Button } from '@repo/design-system-cf/components/button'
import { IconStorefront, IconSchool } from '@repo/design-system-cf/components/icons'
import { useUser, useUserIsDesigner, useUserIsTeacher } from '@repo/cf-user/hooks/use-user-data'
import { usePublicUserProfileQuery } from 'codegen/generated/user'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useGetMyAccountUserProfileQuery } from '@web/gen/cf'
import { ClubhousesSectionV2 } from './clubhouse-section'
import { useClubhousesFromUserQuery } from '@web/gen/crafter'
import { useTranslations } from 'next-intl'

// oxlint-disable-next-line complexity
export function SubUserProfileSection({ isOwn, slug }: { isOwn: boolean; slug?: string }) {
  const t = useTranslations('social-feed.components.feed.post')
  const user = useUser()
  const { data: publicProfileRaw } = usePublicUserProfileQuery(
    { slug: slug ?? user.data?.slug ?? '' },
    { select: d => d?.publicUserProfile, enabled: Boolean(slug ?? user.data?.slug) },
  )
  const publicProfile = publicProfileRaw
  const { data: myAccountProfile } = useGetMyAccountUserProfileQuery(undefined, {
    select: data => data.getMyAccountUserProfile,
    enabled: !!user?.data?.id,
  })
  // Own-only bits
  const userIsDesigner = useUserIsDesigner()
  const userIsTeacher = useUserIsTeacher()

  // Craftings / Machines (own)
  const craftingsMap = React.useMemo(
    // oxlint-disable-next-line unicorn/no-useless-collection-argument
    () => new Map(myAccountProfile?.profile?.craftings?.map(c => [c.key, c.value]) ?? []),
    [myAccountProfile?.profile?.craftings],
  )
  const craftingTypes: string[] = React.useMemo(
    () =>
      (myAccountProfile?.profile?.userCraftings ?? [])
        .map(uc => craftingsMap.get(uc.key))
        // oxlint-disable-next-line unicorn/prefer-native-coercion-functions
        .filter((v): v is string => Boolean(v)),
    [myAccountProfile?.profile?.userCraftings, craftingsMap],
  )

  const machinesMap = React.useMemo(
    // oxlint-disable-next-line unicorn/no-useless-collection-argument
    () => new Map(myAccountProfile?.profile?.machines?.map(m => [m.id, m]) ?? []),
    [myAccountProfile?.profile?.machines],
  )
  const machineTypes: { name: string; brand: string }[] = React.useMemo(
    () =>
      (myAccountProfile?.profile?.userMachines ?? [])
        .map(id => machinesMap.get(id))
        // oxlint-disable-next-line unicorn/prefer-native-coercion-functions
        .filter((m): m is { id: string; name: string; brand: string } => Boolean(m))
        .map(m => ({ name: (m?.name ?? '').trim(), brand: (m?.brand ?? '').trim() })),
    [myAccountProfile?.profile?.userMachines, machinesMap],
  )

  const { data: clubhouses } = useClubhousesFromUserQuery(
    {
      input: { userId: publicProfileRaw?.id as string, page: 0, itemsPerPage: 15 },
    },
    { select: data => data?.clubhousesFromUser?.clubhouses },
  )

  const isClubhouseTabVisible = Boolean(clubhouses && clubhouses.length > 0)
  const userIdForClubs = (isOwn ? user.data?.id : publicProfile?.id) as string

  const isDetailsSectionVisible =
    (isOwn && userIsDesigner) ||
    (isOwn && userIsTeacher) ||
    (isOwn && machineTypes.length > 0) ||
    (isOwn && craftingTypes.length > 0) ||
    isClubhouseTabVisible

  // oxlint-disable-next-line no-nested-ternary
  return isDetailsSectionVisible ? (
    <div className="bg-cf-white rounded-[12px] border border-[#1313151F] relative flex items-center flex-col gap-5 p-4">
      {/* Own-only buttons (Designer/Teacher) */}
      {isOwn && (userIsDesigner || userIsTeacher) ? (
        <div className="flex flex-col gap-2 w-full">
          {userIsDesigner ? (
            <Button variant="secondary" className="w-full">
              <Link
                href={publicProfile?.profileUrls?.designer || ''}
                target="_blank"
                className="w-full flex items-center justify-center gap-1.5"
              >
                <IconStorefront className="size-4" />
                {t('shop_text')}
              </Link>
            </Button>
          ) : null}
          {userIsTeacher ? (
            <Button variant="secondary" className="w-full">
              <Link
                href={publicProfile?.profileUrls?.teacher || ''}
                target="_blank"
                className="w-full flex items-center justify-center gap-1.5"
              >
                <IconSchool className="size-4" />
                {t('classes_text')}
              </Link>
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Own-only: Craftings */}
      {isOwn && craftingTypes.length ? (
        <div className="hidden md:flex flex-col w-full gap-2">
          <div>
            <span className="font-bold text-cf-grey-1000 mr-1 text-14">{t('crafting_types')}</span>
            <span className="text-cf-grey-500 text-10 font-semibold bg-cf-grey-200 border border-[#1313151F] px-1.5 rounded-[24px]">
              {craftingTypes.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {craftingTypes.map(type => (
              <span
                key={type}
                className="inline-flex w-fit border border-cf-blue-100 bg-cf-grey-100 px-2 rounded-[24px] justify-center font-semibold items-center gap-0.5 text-cf-blue-900 text-xs"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Own-only: Machines */}
      {isOwn && machineTypes.length ? (
        <div className="hidden md:flex flex-col w-full gap-2">
          <div>
            <span className="font-bold text-cf-grey-1000 mr-1 text-14">{t('machine_types')}</span>
            <span className="text-cf-grey-500 text-10 font-semibold bg-cf-grey-200 border border-[#1313151F] px-1.5 rounded-[24px]">
              {machineTypes.length}
            </span>
          </div>
          <div className="flex flex-col gap-2 w-full">
            {machineTypes.map(m => (
              <div key={`${m.name}-${m.brand}`} className="border border-[#1313151F] rounded-2 p-3">
                <p className="font-semibold text-[#272B2F] text-14">{m.name}</p>
                <p className="text-[#4B5563] text-11">{m.brand}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Clubhouses */}
      {isClubhouseTabVisible ? <ClubhousesSectionV2 userId={userIdForClubs} /> : null}
    </div>
  ) : isOwn ? (
    <div className="border border-[#1313151F] p-4 rounded-[12px]">
      <p className="font-bold text-18 text-cf-sf-grey-1000 text-center mb-2">
        {t('no_detail_title')}
      </p>
      <p className="text-center text-cf-sf-grey-800">{t('no_detail_text')}</p>
      <Button className="w-full mt-5">
        <Link
          href={AppRoutes.MY_ACCOUNT_PROFILE}
          target="_blank"
          className="w-full flex items-center justify-center gap-1.5"
        >
          {t('not_detail_cta')}
        </Link>
      </Button>
    </div>
  ) : null
}
